import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Card, Row, Col, Typography, Space, Drawer, Input } from 'antd';
import { CheckCircleOutlined, InfoCircleOutlined, BookOutlined, SafetyOutlined, TeamOutlined, SearchOutlined, DownOutlined, CloseOutlined, MenuOutlined, LogoutOutlined, SwapOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

const LandingContainer = styled.div`
  width: 100%;
  background-image: url('/main-bg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  overflow-x: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(11, 14, 17, 0.7);
    z-index: 0;
  }
  
  > * {
    position: relative;
    z-index: 1;
  }
`;

const Header = styled.header`
  background-color: #1E2329;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid #2B3139;
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const HeaderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
`;

const Logo = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #EAECEF;
  display: flex;
  align-items: center;
  gap: 8px;
  
  img {
    height: 28px;
    width: auto;
  }
  
  span {
    line-height: 28px;
    display: inline-block;
  }
`;

const NavMenu = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
  margin: 0 16px;
  cursor: pointer;
  font-weight: 500;
  color: #848E9C;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #0ECB81;
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: #1E2329;
  border: 1px solid #2B3139;
  min-width: 300px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  padding: 16px 0;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;
  z-index: 1001;
  
  ${NavItem}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const SolutionsDropdownContent = styled(DropdownContent)`
  min-width: 400px;
`;

const DropdownSection = styled.div`
  padding: 8px 24px;
`;

const DropdownItem = styled.a`
  display: flex;
  align-items: center;
  padding: 8px 0;
  color: #848E9C;
  transition: color 0.2s ease;
  
  &:hover {
    color: #0ECB81;
  }
`;

const DropdownTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: #EAECEF;
`;


const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled(Button)`
  display: none;
  margin-left: auto;
  color: #EAECEF;
  border-color: #2B3139;
  
  &:hover {
    color: #0ECB81;
    border-color: #0ECB81;
  }
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const HeaderButton = styled(Button)`
  background-color: #0ECB81;
  border-color: #0ECB81;
  color: #0B0E11;
  font-weight: 500;
  
  &:hover {
    background-color: #0fdf39;
    border-color: #0fdf39;
    color: #0B0E11;
  }
  
  &.ant-btn-primary {
    background-color: #0ECB81;
    border-color: #0ECB81;
    color: #0B0E11;
    
    &:hover {
      background-color: #0fdf39;
      border-color: #0fdf39;
      color: #0B0E11;
    }
  }
`;

const DocsButton = styled(Button)`
  background: transparent !important;
  border-color: #0ECB81 !important;
  color: #0ECB81 !important;
  font-weight: 500;
  
  .anticon {
    font-size: 16px;
    color: #0ECB81 !important;
  }
  
  &:hover:not(:disabled) {
    background: rgba(14, 203, 129, 0.1) !important;
    border-color: #0ECB81 !important;
    color: #0ECB81 !important;
    
    .anticon {
      color: #0ECB81 !important;
    }
  }
  
  &:focus:not(:disabled) {
    background: rgba(14, 203, 129, 0.1) !important;
    border-color: #0ECB81 !important;
    color: #0ECB81 !important;
    
    .anticon {
      color: #0ECB81 !important;
    }
  }
  
  &:active:not(:disabled) {
    background: rgba(14, 203, 129, 0.15) !important;
    border-color: #0ECB81 !important;
    color: #0ECB81 !important;
    
    .anticon {
      color: #0ECB81 !important;
    }
  }
`;



const Section = styled.section`
  padding: 80px 0;
  position: relative;
  background-color: #0B0E11;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroSection = styled(Section)`
  background-color: #1E2329;
  padding: 120px 0;
  text-align: center;
  border-bottom: 1px solid #2B3139;
`;

const InfoSection = styled(Section)`
  background-color: #181a20;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const InfoContent = styled.div`
  h3 {
    font-size: 32px;
    font-weight: 700;
    color: #EAECEF;
    margin-bottom: 20px;
  }
  
  p {
    font-size: 16px;
    line-height: 1.8;
    color: #848E9C;
    margin-bottom: 16px;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 24px 0;
    
    li {
      padding: 12px 0;
      font-size: 16px;
      color: #848E9C;
      display: flex;
      align-items: flex-start;
      
      &:before {
        content: "✓";
        color: #0ECB81;
        font-weight: bold;
        margin-right: 12px;
        font-size: 20px;
      }
    }
  }
`;

const InfoImage = styled.div`
  background: linear-gradient(135deg, #0B0E11 0%, #1E2329 50%, #0B0E11 100%);
  border-radius: 12px;
  padding: 40px;
  color: #EAECEF;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid #2B3139;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at top right, rgba(14, 203, 129, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
  
  h4 {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #EAECEF;
    position: relative;
    z-index: 1;
  }
  
  .stat {
    margin: 16px 0;
    padding: 16px;
    background: rgba(11, 14, 17, 0.8);
    border-radius: 8px;
    position: relative;
    z-index: 1;
    backdrop-filter: blur(10px);
    
    .label {
      font-size: 14px;
      margin-bottom: 8px;
      color: #0ECB81;
      font-weight: 500;
    }
    
    .value {
      font-size: 20px;
      font-weight: 600;
      color: #EAECEF;
      line-height: 1.4;
    }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled(Title)`
  font-size: 48px !important;
  font-weight: 700 !important;
  margin-bottom: 24px !important;
  color: #EAECEF !important;
  
  @media (max-width: 768px) {
    font-size: 36px !important;
  }
`;

const HeroSubtitle = styled(Paragraph)`
  font-size: 20px;
  color: #848E9C;
  max-width: 800px;
  margin: 0 auto 40px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CTAButton = styled(Button)`
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  padding: 0 32px;
  border-radius: 4px;
`;

const PrimaryButton = styled(CTAButton)`
  background-color: #0ECB81;
  border-color: #0ECB81;
  color: #0B0E11;
  
  &:hover {
    background-color: #0fdf39;
    border-color: #0fdf39;
    color: #0B0E11;
  }
`;

const SecondaryButton = styled(CTAButton)`
  margin-left: 16px;
  background-color: transparent !important;
  border-color: #2B3139 !important;
  color: #EAECEF !important;
  
  &:hover {
    background-color: #1E2329 !important;
    border-color: #0ECB81 !important;
    color: #0ECB81 !important;
  }
  
  @media (max-width: 576px) {
    margin-left: 0;
    margin-top: 16px;
  }
`;

const SectionTitle = styled(Title)`
  font-size: 36px !important;
  font-weight: 700 !important;
  margin-bottom: 16px !important;
  color: #EAECEF !important;
  
  @media (max-width: 768px) {
    font-size: 28px !important;
  }
`;

const SectionSubtitle = styled(Paragraph)`
  font-size: 18px;
  color: #848E9C;
  max-width: 800px;
  margin-bottom: 48px;
`;

const FeatureCard = styled(Card)`
  height: 100%;
  border-radius: 8px;
  background-color: #1E2329;
  border: 1px solid #2B3139;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    border-color: #0ECB81;
  }
  
  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
    background-color: transparent;
  }
  
  .ant-card-body {
    padding: 24px;
    background-color: transparent;
  }
`;

const FeatureIcon = styled.div`
  font-size: 36px;
  color: #0ECB81;
  margin-bottom: 16px;
`;

const FeatureTitle = styled(Title)`
  font-size: 20px !important;
  font-weight: 600 !important;
  margin-bottom: 12px !important;
  color: #EAECEF !important;
`;

const FeatureDescription = styled(Paragraph)`
  font-size: 16px;
  color: #848E9C;
  margin-bottom: 0;
`;

const FooterSection = styled.footer`
  background-color: #1E2329;
  color: #EAECEF;
  padding: 60px 0 30px;
  border-top: 1px solid #2B3139;
`;

const FooterTitle = styled(Title)`
  font-size: 20px !important;
  font-weight: 600 !important;
  margin-bottom: 24px !important;
  color: #EAECEF !important;
`;

const FooterLink = styled.a`
  color: #848E9C;
  display: block;
  margin-bottom: 12px;
  transition: color 0.3s ease;
  
  &:hover {
    color: #0ECB81;
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 30px;
  margin-top: 30px;
  border-top: 1px solid #2B3139;
  color: #5E6673;
  font-size: 14px;
`;

const LandingPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLoginClick = () => {
    navigate('/signin');
  };

  const handleLogoutClick = () => {
    logout();
  };

  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features-section');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <LandingContainer>

      {/* Header */}
      <Header>
        <HeaderContainer>
          <div style={{ display: 'flex', alignItems: 'center', gap: '48px' }}>
            <Logo>
              <img src="/Algotrade - Black.png" alt="Algotrade" />
              <span style={{ color: '#0ECB81' }}>Fortuna</span>
            </Logo>

            <NavMenu>
              <NavItem>
                Why Fortuna
                <DownOutlined style={{ fontSize: '12px', marginLeft: '5px' }} />
                <DropdownContent>
                  <DropdownSection>
                    <DropdownItem href="https://www.algotrade.vn/about" target="_blank" rel="noopener noreferrer">
                      About Algotrade
                    </DropdownItem>
                    <DropdownItem href="#" onClick={(e) => { e.preventDefault(); scrollToFeatures(); }}>
                      Our Mission
                    </DropdownItem>
                    {/* <DropdownItem href="#">
                    Customer Stories
                  </DropdownItem> */}
                  </DropdownSection>
                </DropdownContent>
              </NavItem>

              <NavItem>
                Solutions
                <DownOutlined style={{ fontSize: '12px', marginLeft: '5px' }} />
                <SolutionsDropdownContent>

                  <DropdownSection>
                    <DropdownTitle>Products</DropdownTitle>
                    <Row gutter={[16, 16]} style={{ marginRight: 8 }}>
                      <Col span={12}>
                        <DropdownItem href="/coming-soon">
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                              <SafetyOutlined style={{ marginRight: '8px', color: '#0ECB81' }} />

                              <strong>Smart Beta</strong>
                            </div>
                            <div style={{ fontSize: '12px', color: '#848E9C', paddingLeft: '24px' }}>
                              Periodic rebalancing of your portfolio to maintain a desired risk-return profile.
                            </div>
                          </div>
                        </DropdownItem>
                      </Col>
                      <Col span={12}>
                        <DropdownItem href="/market-maker">
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                              <SwapOutlined style={{ marginRight: '8px', color: '#0ECB81' }} />
                              <strong>Market Maker</strong>
                            </div>
                            <div style={{ fontSize: '12px', color: '#848E9C', paddingLeft: '24px' }}>
                              Provide liquidity by placing bid and ask orders simultaneously
                            </div>
                          </div>
                        </DropdownItem>
                      </Col>
                      <Col span={12}>
                        <DropdownItem href="/pair-trading">
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                              <TeamOutlined style={{ marginRight: '8px', color: '#0ECB81' }} />
                              <strong>Pair Trading</strong>
                            </div>
                            <div style={{ fontSize: '12px', color: '#848E9C', paddingLeft: '24px' }}>
                              Advanced pair trading with real-time analytics
                            </div>
                          </div>
                        </DropdownItem>
                      </Col>
                      <Col span={12}>
                        <DropdownItem href="/coming-soon">
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                              <BookOutlined style={{ marginRight: '8px', color: '#0ECB81' }} />
                              <strong>Grid Trading</strong>
                            </div>
                            <div style={{ fontSize: '12px', color: '#848E9C', paddingLeft: '24px' }}>
                              Automated capital allocation & risk management
                            </div>
                          </div>
                        </DropdownItem>
                      </Col>
                    </Row>
                  </DropdownSection>
                </SolutionsDropdownContent>
              </NavItem>

              <NavItem>
                Resources
                <DownOutlined style={{ fontSize: '12px', marginLeft: '5px' }} />
                <DropdownContent>
                  <DropdownSection>
                    <DropdownItem href="https://hub.algotrade.vn/" target="_blank" rel="noopener noreferrer">
                      <InfoCircleOutlined style={{ marginRight: '8px' }} />
                      Knowledge Hub
                    </DropdownItem>

                    <DropdownItem href="#">
                      <CheckCircleOutlined style={{ marginRight: '8px' }} />
                      Support Center
                    </DropdownItem>
                  </DropdownSection>
                </DropdownContent>
              </NavItem>
            </NavMenu>
          </div>

          <HeaderActions>
            <DocsButton
              type="default"
              icon={<FileTextOutlined style={{ fontSize: '16px' }} />}
              style={{ height: "32px", padding: '0 16px' }}
              onClick={() => window.open('https://hub.algotrade.vn/', '_blank')}
            >
              Docs
            </DocsButton>
            {!isAuthenticated ? (
              <HeaderButton type="primary" style={{ height: "32px" }} onClick={handleLoginClick}>
                Log In
              </HeaderButton>
            ) : (
              <HeaderButton
                type="primary"
                style={{ height: "32px" }}
                onClick={handleLogoutClick}
                icon={<LogoutOutlined />}
              >
                Log Out
              </HeaderButton>
            )}
          </HeaderActions>

          <MobileMenuButton type="text" icon={<MenuOutlined />} onClick={toggleMobileMenu} />

          {/* Mobile Menu Drawer */}
          <Drawer
            title="Menu"
            placement="right"
            onClose={toggleMobileMenu}
            open={mobileMenuOpen}
            width={300}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '16px' }}>Platform</div>
                  <div style={{ paddingLeft: '16px' }}>
                    <div style={{ marginBottom: '12px' }}><a href="/">Home</a></div>
                    <div style={{ marginBottom: '12px' }}><a href="/pair-trading">Pair Trading</a></div>
                    <div style={{ marginBottom: '12px' }}><a href="#">About Us</a></div>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '16px' }}>Trading Features</div>
                  <div style={{ paddingLeft: '16px' }}>
                    <div style={{ marginBottom: '12px' }}><a href="/pair-trading">Pair Trading</a></div>
                    <div style={{ marginBottom: '12px' }}><a href="#">Position Sizing</a></div>
                    <div style={{ marginBottom: '12px' }}><a href="#">Risk Analytics</a></div>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '16px' }}>Resources</div>
                  <div style={{ paddingLeft: '16px' }}>
                    <div style={{ marginBottom: '12px' }}><a href="#">Blog</a></div>
                    {/* <div style={{ marginBottom: '12px' }}><a href="#">Webinars</a></div> */}
                    <div style={{ marginBottom: '12px' }}><a href="#">Support Center</a></div>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '16px' }}>Partners</div>
                  <div style={{ paddingLeft: '16px' }}>
                    <div style={{ marginBottom: '12px' }}><a href="#">Partner Programs</a></div>
                    <div style={{ marginBottom: '12px' }}><a href="#">Integration Partners</a></div>
                    <div style={{ marginBottom: '12px' }}><a href="#">Become a Partner</a></div>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '16px' }}><a href="#">Media Center</a></div>
                </div>
              </div>

              <div>
                {!isAuthenticated ? (
                  <Button type="primary" block style={{ marginBottom: '12px' }} onClick={handleLoginClick}>
                    Log In
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    block
                    style={{ marginBottom: '12px' }}
                    onClick={handleLogoutClick}
                    icon={<LogoutOutlined />}
                  >
                    Log Out
                  </Button>
                )}
                <Button block style={{ marginBottom: '12px' }}>Contact Sales</Button>
                <Button block>Support</Button>
              </div>
            </div>
          </Drawer>
        </HeaderContainer>
      </Header>
      {/* Hero Section */}
      <HeroSection>
        <Container>
          <HeroTitle>Advanced your strategy with optimal execution</HeroTitle>
          <HeroSubtitle>
            Execute advanced trading strategies with real-time data, dynamic position sizing, and automated risk control.
            The system unifies futures and equities to capture market inefficiencies with speed and precision.
          </HeroSubtitle>
          <Space wrap>
            <PrimaryButton type="primary" size="large" onClick={() => window.location.href = '/pair-trading'}>
              Get Started
            </PrimaryButton>
            <SecondaryButton size="large">
              View Demo
            </SecondaryButton>
          </Space>
        </Container>
      </HeroSection>

      {/* Features Section */}
      <Section id="features-section">
        <Container>
          <SectionTitle>Powerful Algorithmic Trading Features</SectionTitle>
          <SectionSubtitle>
            Our platform provides comprehensive tools for executing algorithmic trading strategies with precision,
            combining real-time market data, intelligent analytics, and automated risk management.
          </SectionSubtitle>

          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} lg={8}>
              <FeatureCard>
                <FeatureIcon>
                  <SearchOutlined />
                </FeatureIcon>
                <FeatureTitle level={4}>Smart Portfolio Allocation</FeatureTitle>
                <FeatureDescription>
                  Deliver a balanced risk-return portfolio with high alpha that confidently outperforms the market.
                  Periodic rebalancing with low slippage and real-time optimal pricing to enhance portfolio performance.
                </FeatureDescription>
              </FeatureCard>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <FeatureCard>
                <FeatureIcon>
                  <SafetyOutlined />
                </FeatureIcon>
                <FeatureTitle level={4}>Automated Position Sizing</FeatureTitle>
                <FeatureDescription>
                  Configure your trading parameters including basis points, take profit, and stop loss levels.
                  Our system automatically calculates optimal position sizes and capital allocation across your selected instruments.
                </FeatureDescription>
              </FeatureCard>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <FeatureCard>
                <FeatureIcon>
                  <BookOutlined />
                </FeatureIcon>
                <FeatureTitle level={4}>Real-time Risk Analytics</FeatureTitle>
                <FeatureDescription>
                  Monitor your pair trading positions with live P&L projections, risk/reward ratios, and portfolio delta.
                  Get instant feedback on potential profits and losses before executing trades.
                </FeatureDescription>
              </FeatureCard>
            </Col>
          </Row>
        </Container>
      </Section>

      <InfoSection>
        <Container>
          <InfoGrid>
            <InfoContent>
              <h3>What is Pair Trading?</h3>
              <p>
                Pair trading is a market-neutral trading strategy that involves simultaneously buying and selling
                two correlated instruments to profit from the price difference (basis) between them.
              </p>
              <p>
                By combining a long position in one instrument with a short position in another, traders can
                minimize market risk while capitalizing on relative price movements.
              </p>
              <ul>
                <li>Market-neutral strategy reduces directional risk</li>
                <li>Profit from basis convergence or divergence</li>
                <li>Hedge against market volatility</li>
                <li>Suitable for futures and stock combinations</li>
              </ul>
            </InfoContent>
            <InfoImage>
              <h4>Key Trading Metrics</h4>
              <div className="stat">
                <div className="label">Basis Point</div>
                <div className="value">Price difference between futures and spot</div>
              </div>
              <div className="stat">
                <div className="label">Position Sizing</div>
                <div className="value">Automated capital allocation</div>
              </div>
              <div className="stat">
                <div className="label">Risk Management</div>
                <div className="value">Real-time P&L tracking</div>
              </div>
            </InfoImage>
          </InfoGrid>
        </Container>
      </InfoSection>

      {/* Testimonials Section */}


      <FooterSection>
        <Container>
          <Row gutter={[48, 32]}>
            <Col xs={24} sm={12} md={6}>
              <FooterTitle level={4}>Features</FooterTitle>
              <FooterLink href="/pair-trading">Pair Trading</FooterLink>
              <FooterLink href="/coming-soon">Grid</FooterLink>
              <FooterLink href="/coming-soon">Smart Beta</FooterLink>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <FooterTitle level={4}>Resources</FooterTitle>
              <FooterLink href="#">Documentation</FooterLink>
              <FooterLink href="#">API Reference</FooterLink>
              <FooterLink href="#">Video Tutorials</FooterLink>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <FooterTitle level={4}>Markets</FooterTitle>
              <FooterLink href="#">Futures</FooterLink>
              <FooterLink href="#">Stocks</FooterLink>
            </Col>


            <Col xs={24} sm={12} md={6}>
              <FooterTitle level={4}>Company</FooterTitle>

              <FooterLink href="#">Support</FooterLink>
              <FooterLink href="#">Contact Us</FooterLink>
            </Col>
          </Row>

          <Copyright>
            © {new Date().getFullYear()} Algotrade. All rights reserved.
          </Copyright>
        </Container>
      </FooterSection>
    </LandingContainer>
  );
};

export default LandingPage;